// iter192 — Profit & Loss dashboard section.
//
// Premium, luxury-feeling P&L overview: an elegant donut chart with
// NET PROFIT / NET LOSS + amount in the center, a clean legend, and a
// compact per-shoot profitability list underneath. Uses only pure SVG
// arc math — no chart libraries and NO bars/strips (explicit user rule).
//
// Data source (cash-basis, matches iter51):
//   revenue  = Σ payment_milestones (received/paid/completed)
//   expenses = Σ payroll_payments.paid + Σ office_expenses.debit
//   net      = revenue − expenses
//
// Backend: GET /api/dashboard/pnl?period=today|week|month|last30|year
//          GET /api/dashboard/shoot-profitability?period=...&limit=12
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/utils";
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, MinusCircle } from "lucide-react";
import ProfitTrendSparkline from "@/components/ProfitTrendSparkline";
import PnLDrillDrawer from "@/components/PnLDrillDrawer";

const PERIODS = [
  { key: "today",  label: "Today" },
  { key: "week",   label: "This Week" },
  { key: "month",  label: "This Month" },
  { key: "last30", label: "Last 30 Days" },
  { key: "year",   label: "This Year" },
];

// SVG donut geometry — soft off-white studio palette.
const SIZE = 240;
const STROKE = 26;
const RADIUS = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

function Donut({ revenue, expenses, isProfit, hoverKey, setHoverKey }) {
  const total = Math.max(revenue + expenses, 0.0001); // avoid div/0
  const revShare = revenue / total;
  const expShare = expenses / total;
  const revLen = revShare * CIRC;
  const expLen = expShare * CIRC;
  // Rotate so revenue starts at 12 o'clock (−90°).
  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="w-56 h-56 mx-auto -rotate-90"
      role="img"
      aria-label="Profit and Loss donut chart"
    >
      {/* Track: subtle stone ring so 0/0 state still reads. */}
      <circle
        cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
        fill="none"
        stroke="rgb(231 229 228 / 0.7)"
        strokeWidth={STROKE}
      />
      {/* Revenue arc — sage/emerald tone, subtle luxury. */}
      <circle
        cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
        fill="none"
        stroke={isProfit ? "rgb(4 120 87)" : "rgb(15 118 110)"}
        strokeWidth={hoverKey === "revenue" ? STROKE + 4 : STROKE}
        strokeDasharray={`${revLen} ${CIRC - revLen}`}
        strokeDashoffset={0}
        strokeLinecap="butt"
        style={{ transition: "stroke-width 220ms ease, opacity 220ms ease",
                 opacity: hoverKey && hoverKey !== "revenue" ? 0.35 : 1,
                 cursor: "pointer" }}
        onMouseEnter={() => setHoverKey("revenue")}
        onMouseLeave={() => setHoverKey(null)}
        data-testid="pnl-donut-revenue-arc"
      />
      {/* Expenses arc — muted terracotta/rose, warning tone if loss. */}
      <circle
        cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
        fill="none"
        stroke={isProfit ? "rgb(180 83 9)" : "rgb(190 18 60)"}
        strokeWidth={hoverKey === "expenses" ? STROKE + 4 : STROKE}
        strokeDasharray={`${expLen} ${CIRC - expLen}`}
        strokeDashoffset={-revLen}
        strokeLinecap="butt"
        style={{ transition: "stroke-width 220ms ease, opacity 220ms ease",
                 opacity: hoverKey && hoverKey !== "expenses" ? 0.35 : 1,
                 cursor: "pointer" }}
        onMouseEnter={() => setHoverKey("expenses")}
        onMouseLeave={() => setHoverKey(null)}
        data-testid="pnl-donut-expenses-arc"
      />
    </svg>
  );
}

function LegendRow({ colorClass, label, amount, share, testid, isEmphasis, hover, onHover, onClick }) {
  return (
    <button
      type="button"
      onMouseEnter={() => onHover(hover)}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
      className={`flex items-center justify-between w-full px-3 py-2.5 rounded-md text-left transition cursor-pointer ${
        isEmphasis ? "bg-stone-50 hover:bg-stone-100" : "hover:bg-stone-50"
      }`}
      data-testid={testid}
      title="Click to see the transactions behind this number"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${colorClass} shrink-0`} />
        <span className="text-[11px] uppercase tracking-[0.14em] text-stone-600 truncate">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-2 shrink-0">
        <span className={`font-mono text-sm tabular-nums ${isEmphasis ? "font-semibold text-stone-900" : "text-stone-800"}`}>
          {inr(amount)}
        </span>
        {share !== undefined && (
          <span className="text-[10px] text-stone-400 tabular-nums w-9 text-right">
            {share.toFixed(1)}%
          </span>
        )}
      </div>
    </button>
  );
}

function ShootProfitCard({ shoot }) {
  if (!shoot.has_data) {
    return (
      <div className="border border-stone-200 rounded-lg px-3.5 py-3 bg-stone-50/50" data-testid={`shoot-profit-row-${shoot.booking_id}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium text-sm text-stone-800 truncate">
              {shoot.client_name || "—"}
            </div>
            <div className="text-[10px] text-stone-500 uppercase tracking-wider mt-0.5">
              {shoot.date} · {shoot.event || "—"}
            </div>
          </div>
          <div className="text-[11px] text-stone-400 italic shrink-0 flex items-center gap-1">
            <MinusCircle className="w-3 h-3" strokeWidth={1.8} />
            Data unavailable
          </div>
        </div>
      </div>
    );
  }
  const isProfit = shoot.net >= 0;
  const Arrow = isProfit ? ArrowUpRight : ArrowDownRight;
  return (
    <div
      className={`border rounded-lg px-3.5 py-3 hover:shadow-sm transition ${
        isProfit
          ? "border-stone-200 bg-white"
          : "border-rose-200 bg-rose-50/30"
      }`}
      data-testid={`shoot-profit-row-${shoot.booking_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm text-stone-800 truncate">
            {shoot.client_name}
          </div>
          <div className="text-[10px] text-stone-500 uppercase tracking-wider mt-0.5">
            {shoot.date} · {shoot.event || "shoot"}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`inline-flex items-center gap-1 font-mono text-sm font-semibold tabular-nums ${
            isProfit ? "text-emerald-700" : "text-rose-700"
          }`}>
            <Arrow className="w-3.5 h-3.5" strokeWidth={2} />
            {inr(Math.abs(shoot.net))}
          </div>
          <div className={`text-[10px] tabular-nums mt-0.5 ${
            isProfit ? "text-emerald-700/70" : "text-rose-700/70"
          }`}>
            {shoot.margin_pct.toFixed(1)}% margin
          </div>
        </div>
      </div>
      <div className="mt-2.5 pt-2.5 border-t border-stone-100 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[9px] uppercase tracking-[0.12em] text-stone-500">Revenue</div>
          <div className="font-mono text-xs tabular-nums text-stone-800 mt-0.5">{inr(shoot.revenue)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.12em] text-stone-500">Crew cost</div>
          <div className="font-mono text-xs tabular-nums text-stone-800 mt-0.5">{inr(shoot.crew_cost)}</div>
        </div>
      </div>
    </div>
  );
}

export default function ProfitLossOverview() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [shoots, setShoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoverKey, setHoverKey] = useState(null);
  // iter195 — drill drawer state.
  const [drillBucket, setDrillBucket] = useState(null);  // "revenue" | "payroll" | "office" | "net" | null

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/dashboard/pnl?period=${period}`),
      api.get(`/dashboard/shoot-profitability?period=${period}&limit=12`),
    ]).then(([pnlRes, sRes]) => {
      if (cancelled) return;
      setData(pnlRes.data);
      setShoots(sRes.data.shoots || []);
    }).catch(() => {
      // Silent fail — parent dashboard already surfaces global errors.
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const revenue = data?.revenue?.total ?? 0;
  const expenses = data?.expenses?.total ?? 0;
  const net = data?.net ?? 0;
  const isProfit = net >= 0;
  const totalForShare = Math.max(revenue + expenses, 0.0001);
  const revShare = (revenue / totalForShare) * 100;
  const expShare = (expenses / totalForShare) * 100;

  const sortedShoots = useMemo(() => {
    // Highest-profit first; loss shoots pushed to end sorted by loss size.
    const withData = shoots.filter((s) => s.has_data);
    const noData = shoots.filter((s) => !s.has_data);
    withData.sort((a, b) => b.net - a.net);
    return [...withData, ...noData];
  }, [shoots]);

  return (
    <div className="card overflow-hidden" data-testid="pnl-overview">
      {/* Header + Period selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-stone-200 bg-gradient-to-r from-stone-50/40 to-transparent">
        <div className="flex items-center gap-2">
          {isProfit ? (
            <TrendingUp className="w-4 h-4 text-emerald-700" strokeWidth={1.7} />
          ) : (
            <TrendingDown className="w-4 h-4 text-rose-700" strokeWidth={1.7} />
          )}
          <h2 className="font-heading text-base font-semibold tracking-tight">
            Profit &amp; Loss
          </h2>
          <span className="text-[10px] uppercase tracking-[0.14em] text-stone-400 hidden sm:inline">
            · {data?.period_label || "…"}
          </span>
        </div>
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Period selector">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriod(p.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] tracking-wide transition border ${
                period === p.key
                  ? "bg-stone-900 text-stone-50 border-stone-900"
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-400 hover:text-stone-900"
              }`}
              data-testid={`pnl-period-${p.key}`}
              aria-pressed={period === p.key}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Donut + Legend + Sparkline (iter193) */}
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_280px] gap-6 lg:gap-8 px-5 py-6 items-center">
        {/* Donut wrapper — center overlay */}
        <div className="relative mx-auto lg:mx-0" style={{ width: 224, height: 224 }}>
          <Donut
            revenue={revenue}
            expenses={expenses}
            isProfit={isProfit}
            hoverKey={hoverKey}
            setHoverKey={setHoverKey}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className={`text-[10px] uppercase tracking-[0.18em] font-medium ${
              isProfit ? "text-emerald-700" : "text-rose-700"
            }`} data-testid="pnl-center-label">
              {isProfit ? "Net Profit" : "Net Loss"}
            </div>
            <div className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tabular-nums mt-1" data-testid="pnl-center-amount">
              {inr(Math.abs(net))}
            </div>
            {loading && (
              <div className="text-[9px] uppercase tracking-widest text-stone-400 mt-1">
                Loading…
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="space-y-1 min-w-0">
          <LegendRow
            colorClass={isProfit ? "bg-emerald-700" : "bg-teal-700"}
            label="Client Revenue"
            amount={revenue}
            share={revShare}
            testid="pnl-legend-revenue"
            hover="revenue"
            onHover={setHoverKey}
            onClick={() => setDrillBucket("revenue")}
          />
          <LegendRow
            colorClass={isProfit ? "bg-amber-600" : "bg-rose-700"}
            label="Total Expenses"
            amount={expenses}
            share={expShare}
            testid="pnl-legend-expenses"
            hover="expenses"
            onHover={setHoverKey}
            onClick={() => setDrillBucket("net")}
          />
          <div className="h-px bg-stone-200 my-1.5 mx-3" />
          <button
            type="button"
            onClick={() => setDrillBucket("net")}
            className={`flex items-center justify-between w-full px-3 py-2.5 rounded-md text-left transition cursor-pointer ${
              isProfit ? "bg-emerald-50/70 hover:bg-emerald-100/70" : "bg-rose-50/70 hover:bg-rose-100/70"
            }`}
            data-testid="pnl-legend-net"
            title="Click to open the Net Profit / Loss breakdown"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className={`text-[11px] uppercase tracking-[0.14em] font-semibold ${
                isProfit ? "text-emerald-800" : "text-rose-800"
              }`}>
                {isProfit ? "Net Profit" : "Net Loss"}
              </span>
            </div>
            <span className={`font-mono text-sm font-semibold tabular-nums ${
              isProfit ? "text-emerald-800" : "text-rose-800"
            }`}>
              {isProfit ? "+" : "−"}{inr(Math.abs(net))}
            </span>
          </button>
          {/* Expense breakdown — small print for the accountant.
             iter195 — each mini-tile is clickable to drill into that expense bucket. */}
          {data && (
            <div className="pt-2 mt-1 border-t border-stone-100 grid grid-cols-2 gap-3 px-3 pb-1">
              <button
                type="button"
                onClick={() => setDrillBucket("payroll")}
                className="text-left rounded p-1 -m-1 hover:bg-stone-50 transition cursor-pointer"
                data-testid="pnl-legend-payroll"
                title="Click to see every payroll payment"
              >
                <div className="text-[9px] uppercase tracking-[0.14em] text-stone-500">Payroll paid</div>
                <div className="font-mono text-xs tabular-nums text-stone-800 mt-0.5">
                  {inr(data.expenses.payroll.total)}
                </div>
              </button>
              <button
                type="button"
                onClick={() => setDrillBucket("office")}
                className="text-left rounded p-1 -m-1 hover:bg-stone-50 transition cursor-pointer"
                data-testid="pnl-legend-office"
                title="Click to see every office expense"
              >
                <div className="text-[9px] uppercase tracking-[0.14em] text-stone-500">Office expenses</div>
                <div className="font-mono text-xs tabular-nums text-stone-800 mt-0.5">
                  {inr(data.expenses.office.total)}
                </div>
              </button>
            </div>
          )}
        </div>

        {/* iter193 — 6-month Net Profit trend sparkline (desktop: right column, mobile: stacks). */}
        <div className="min-w-0">
          <ProfitTrendSparkline />
        </div>
      </div>

      {/* Shoot Profitability — compact list */}
      <div className="border-t border-stone-200 px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-stone-700">
            Shoot profitability
          </div>
          <div className="text-[10px] text-stone-400">
            {sortedShoots.length} shoot{sortedShoots.length === 1 ? "" : "s"} · {data?.period_label || ""}
          </div>
        </div>
        {sortedShoots.length === 0 ? (
          <div className="text-center py-6 text-xs text-stone-400 italic" data-testid="pnl-shoots-empty">
            No shoots in this period yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5" data-testid="pnl-shoots-grid">
            {sortedShoots.slice(0, 12).map((s) => (
              <ShootProfitCard key={s.booking_id} shoot={s} />
            ))}
          </div>
        )}
      </div>

      {/* iter195 — drill-down drawer (revenue / payroll / office / net) */}
      <PnLDrillDrawer
        open={!!drillBucket}
        bucket={drillBucket}
        period={period}
        periodLabel={data?.period_label || ""}
        onClose={() => setDrillBucket(null)}
        onSwitchBucket={(next) => setDrillBucket(next)}
      />
    </div>
  );
}
